import React from 'react';
import { User } from '@citrite/sf-api';
import { Tab } from '@sharefiledev/flow-web';
import type {
	CreateClientButtonExtensionParams,
	DeletedProjectResourcesExtensionsParams,
	ProjectSpecEditorExtensionParams,
	ProjectTabsExtensionParams,
	ProjectTilesExtensionParams,
} from '@sharefiledev/projects-pilet-sdk';
import type { PiletApi } from '@sharefiledev/sharefile-appshell';
import { tab } from '../enum';
import { LoadingSpinner } from '../LoadingSpinner';
import { useProjectSpecDataProvider } from '../PluginDataProvider';
import type { ShortcutActionButtonProps } from '../ShortcutActionButton/ShortcutActionButton';
import { t } from '../translate';

interface ExtensionProps {
	piletApi: PiletApi;
}

const LazyHomeTabExtension = React.lazy(() => import('./HomeTabExtension'));
export const AsyncHomeTabExtension: React.FC<ProjectTabsExtensionParams> = props => {
	return (
		<Tab tabContext={props.tabContext} value={tab.home} label={t('ProjectTabs.home')}>
			<LazyHomeTabExtension {...props} />
		</Tab>
	);
};

const LazyCreateClientButtonExtension = React.lazy(
	() => import('./CreateClientButtonExtension')
);

export const AsyncCreateClientButtonExtension: React.FC<
	CreateClientButtonExtensionParams
> = ({ entityType, setEntityPickerValue }) => {
	if (entityType !== 'ShareFile/sharefile/Client') {
		return null;
	}

	return (
		<LazyCreateClientButtonExtension
			createClientCallback={(client: User) => setEntityPickerValue(client)}
		/>
	);
};

const LazyFilesTabExtension = React.lazy(() => import('./FilesTabExtension'));
export const AsyncFilesTabExtension: React.FC<
	ProjectTabsExtensionParams & ExtensionProps
> = props => {
	return (
		<Tab tabContext={props.tabContext} value={tab.files} label={t('ProjectTabs.files')}>
			<LazyFilesTabExtension {...props} />
		</Tab>
	);
};

const LazyFilesTileExtension = React.lazy(() => import('./FilesTileExtension'));
export const AsyncFilesTileExtension: React.FC<ProjectTilesExtensionParams> = props => (
	<LazyFilesTileExtension {...props} />
);

const LazyDeletedFilesTabExtension = React.lazy(
	() => import('./DeletedFilesTabExtension')
);
export const AsyncDeletedFilesTabExtension: React.FC<
	DeletedProjectResourcesExtensionsParams
> = props => {
	return (
		<Tab tabContext={props.tabContext} value={'files'} label={t('DeletedFilesTab.title')}>
			<LazyDeletedFilesTabExtension />
		</Tab>
	);
};

const LazyShortcutActionButtonExtension = React.lazy(
	() => import('./ShortcutActionButtonExtension')
);
export const AsyncShortcutActionButtonExtension: React.FC<
	ShortcutActionButtonProps & ExtensionProps
> = props => <LazyShortcutActionButtonExtension {...props} />;

const LazySpecEditorExtensionSlot = React.lazy(
	() => import('./ProjectSpecEditorExtension')
);
export const AsyncSpecEditorExtension: React.FC<
	ExtensionProps & ProjectSpecEditorExtensionParams
> = props => {
	const dataProvider = useProjectSpecDataProvider({
		resourceSpec: props.resourceSpec,
		onUpdate: props.onUpdate,
	});
	return (
		<React.Suspense fallback={<LoadingSpinner />}>
			<LazySpecEditorExtensionSlot {...props} dataProvider={dataProvider} />
		</React.Suspense>
	);
};
